import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, Ticket, Location, formatTicketRef } from '../lib/api';
import { 
  Plus, Search, X, AlertCircle, Wrench, 
  Clock, MapPin, Package, ChevronRight, Timer, CheckCircle,
  Flame, ChevronDown, Check, Hash
} from 'lucide-react';
import { DataTable, Column, PriorityDot, Toolbar, ResultsCount } from '../components/DataTable';
import { useViewMode } from '../contexts/ViewModeContext';

const statusConfig: Record<string, { label: string; className: string }> = {
  open: { label: 'Aperto', className: 'badge-amber' },
  in_progress: { label: 'In Corso', className: 'badge-blue' },
  waiting: { label: 'In Attesa', className: 'badge-orange' },
  resolved: { label: 'Risolto', className: 'badge-green' },
  closed: { label: 'Chiuso', className: 'badge-gray' },
  cancelled: { label: 'Annullato', className: 'badge-red' }
};

const priorityLabels: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Media',
  low: 'Bassa'
};

// Multi-select location filter component with search
function LocationMultiSelect({ 
  locations, 
  selected, 
  onChange 
}: { 
  locations: Location[]; 
  selected: string[]; 
  onChange: (ids: string[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredLocations = locations.filter(loc => 
    loc.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggleLocation = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const selectedNames = selected
    .map(id => locations.find(l => l.id === id)?.name)
    .filter(Boolean)
    .slice(0, 2);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="input input-sm w-full text-left flex items-center justify-between gap-2"
      >
        <span className="truncate text-sm">
          {selected.length === 0 ? (
            <span className="text-gray-500">Ubicazione</span>
          ) : selected.length === 1 ? (
            selectedNames[0]
          ) : (
            `${selectedNames.join(', ')}${selected.length > 2 ? ` +${selected.length - 2}` : ''}`
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca ubicazione..."
                className="input input-sm pl-7 w-full"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredLocations.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 text-center">Nessuna ubicazione trovata</div>
            ) : (
              filteredLocations.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => toggleLocation(loc.id)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    selected.includes(loc.id) 
                      ? 'bg-primary-500 border-primary-500 text-white' 
                      : 'border-gray-300'
                  }`}>
                    {selected.includes(loc.id) && <Check className="w-3 h-3" />}
                  </div>
                  <span className="text-sm text-gray-700 truncate">{loc.name}</span>
                </button>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Deseleziona tutto
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Tickets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useViewMode();
  
  // Get initial values from URL
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || '');
  const [locationFilter, setLocationFilter] = useState<string[]>(() => {
    const loc = searchParams.get('location');
    return loc ? loc.split(',').filter(Boolean) : [];
  });

  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (statusFilter) params.set('status', statusFilter);
  if (priorityFilter) params.set('priority', priorityFilter);
  if (locationFilter.length > 0) params.set('location', locationFilter.join(','));

  const { data, isLoading, error } = useQuery({
    queryKey: ['tickets', search, statusFilter, priorityFilter, locationFilter],
    queryFn: () => api.getTickets(params),
    refetchOnMount: 'always'
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
    staleTime: 5 * 60 * 1000
  });

  // Update URL when filters change
  const updateFilters = (newFilters: { status?: string; priority?: string; locations?: string[]; search?: string }) => {
    const newParams = new URLSearchParams();
    const newStatus = newFilters.status !== undefined ? newFilters.status : statusFilter;
    const newPriority = newFilters.priority !== undefined ? newFilters.priority : priorityFilter;
    const newLocations = newFilters.locations !== undefined ? newFilters.locations : locationFilter;
    const newSearch = newFilters.search !== undefined ? newFilters.search : search;
    
    if (newSearch) newParams.set('search', newSearch);
    if (newStatus) newParams.set('status', newStatus);
    if (newPriority) newParams.set('priority', newPriority);
    if (newLocations.length > 0) newParams.set('location', newLocations.join(','));
    
    setSearchParams(newParams);
    if (newFilters.status !== undefined) setStatusFilter(newFilters.status);
    if (newFilters.priority !== undefined) setPriorityFilter(newFilters.priority);
    if (newFilters.locations !== undefined) setLocationFilter(newFilters.locations);
    if (newFilters.search !== undefined) setSearch(newFilters.search);
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPriorityFilter('');
    setLocationFilter([]);
    setSearchParams(new URLSearchParams());
  };

  const hasFilters = search || statusFilter || priorityFilter || locationFilter.length > 0;

  const formatDate = (date: string | number) => {
    return new Date(date).toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit',
      year: '2-digit'
    });
  };

  const formatTimeAgo = (date: string | number) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}g`;
    return formatDate(date);
  };

  if (error) {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Errore: {error.message}</span>
        </div>
      </div>
    );
  }

  const tickets = data?.tickets || [];
  const stats = data?.stats || { open: 0, inProgress: 0, urgent: 0, waiting: 0 };

  // Calculate stats from all tickets (not filtered)
  const urgentOpenCount = stats.urgent;
  const openCount = stats.open;
  const inProgressCount = stats.inProgress;
  const waitingCount = stats.waiting;

  // Quick stat card component
  const StatCard = ({ 
    label, 
    value, 
    icon: Icon, 
    color, 
    filterStatus, 
    filterPriority 
  }: { 
    label: string; 
    value: number; 
    icon: React.ElementType; 
    color: string;
    filterStatus?: string;
    filterPriority?: string;
  }) => {
    const isActive = (filterStatus && statusFilter === filterStatus && !priorityFilter) || 
                     (filterPriority && priorityFilter === filterPriority && statusFilter === 'open');
    
    const colorClasses: Record<string, string> = {
      red: 'text-red-600 bg-red-50 hover:bg-red-100 border-red-200',
      amber: 'text-amber-600 bg-amber-50 hover:bg-amber-100 border-amber-200',
      blue: 'text-blue-600 bg-blue-50 hover:bg-blue-100 border-blue-200',
      orange: 'text-orange-600 bg-orange-50 hover:bg-orange-100 border-orange-200',
    };

    return (
      <button
        onClick={() => {
          if (filterPriority) {
            updateFilters({ status: 'open', priority: filterPriority });
          } else if (filterStatus) {
            updateFilters({ status: filterStatus, priority: '' });
          }
        }}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer ${colorClasses[color]} ${isActive ? 'ring-2 ring-offset-1 ring-current scale-105' : ''}`}
      >
        <Icon className="w-4 h-4" />
        <div className="text-left">
          <span className="text-lg font-semibold">{value}</span>
          <span className="text-xs ml-1 opacity-75">{label}</span>
        </div>
      </button>
    );
  };

  const columns: Column<Ticket>[] = [
    {
      key: 'priority',
      header: '',
      width: '40px',
      render: (ticket) => <PriorityDot priority={ticket.priority} />
    },
    {
      key: 'ref',
      header: 'Rif.',
      width: '80px',
      render: (ticket) => (
        <span className="font-mono text-xs text-gray-500">{formatTicketRef(ticket.ticketNumber)}</span>
      )
    },
    {
      key: 'title',
      header: 'Titolo',
      sortable: true,
      render: (ticket) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">{ticket.title}</div>
          {ticket.description && (
            <div className="text-xs text-gray-500 truncate max-w-xs">{ticket.description}</div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Stato',
      width: '110px',
      render: (ticket) => {
        const s = statusConfig[ticket.status] || statusConfig.open;
        return <span className={`badge ${s.className}`}>{s.label}</span>;
      }
    },
    {
      key: 'location',
      header: 'Ubicazione',
      hideOnMobile: true,
      render: (ticket) => {
        const location = ticket.location || ticket.asset?.location;
        return location ? (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <MapPin className="w-3 h-3" />
            <span className="truncate max-w-[100px]">{location.name}</span>
          </div>
        ) : <span className="text-gray-400">-</span>;
      }
    },
    {
      key: 'asset',
      header: 'Asset',
      hideOnMobile: true,
      render: (ticket) => ticket.asset ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <Package className="w-3 h-3" />
          <span className="truncate max-w-[120px]">{ticket.asset.name}</span>
        </div>
      ) : <span className="text-gray-400">-</span>
    },
    {
      key: 'assignedTo',
      header: 'Assegnato',
      hideOnMobile: true,
      width: '100px',
      render: (ticket) => ticket.assignedToUser ? (
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-medium">
            {ticket.assignedToUser.firstName[0]}{ticket.assignedToUser.lastName[0]}
          </div>
          <span className="text-xs text-gray-600 truncate max-w-[60px]">
            {ticket.assignedToUser.firstName}
          </span>
        </div>
      ) : <span className="text-gray-400 text-xs">-</span>
    },
    {
      key: 'updatedAt',
      header: 'Aggiornato',
      width: '90px',
      sortable: true,
      hideOnMobile: true,
      render: (ticket) => (
        <span className="text-xs text-gray-500">{formatTimeAgo(ticket.updatedAt)}</span>
      )
    },
    {
      key: 'createdAt',
      header: 'Creato',
      width: '70px',
      sortable: true,
      hideOnMobile: true,
      render: (ticket) => (
        <span className="text-xs text-gray-500">{formatTimeAgo(ticket.createdAt)}</span>
      )
    },
    {
      key: 'actions',
      header: '',
      width: '30px',
      render: () => <ChevronRight className="w-4 h-4 text-gray-400" />
    }
  ];

  // Mobile card render
  const mobileCardRender = (ticket: Ticket) => {
    const s = statusConfig[ticket.status] || statusConfig.open;
    const location = ticket.location || ticket.asset?.location;
    return (
      <div className="mobile-card">
        <div className="mobile-card-header">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <PriorityDot priority={ticket.priority} />
            <span className="mobile-card-title truncate">{ticket.title}</span>
          </div>
          <span className={`badge ${s.className}`}>{s.label}</span>
        </div>
        {ticket.description && (
          <p className="text-xs text-gray-500 mb-2 line-clamp-1">{ticket.description}</p>
        )}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span className="font-mono text-gray-400">{formatTicketRef(ticket.ticketNumber)}</span>
            {location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {location.name}
              </span>
            )}
            {ticket.asset && (
              <span className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                {ticket.asset.name}
              </span>
            )}
          </div>
          <span>{formatTimeAgo(ticket.createdAt)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Ticket</h1>
          <p className="page-subtitle">{tickets.length} ticket totali</p>
        </div>
        <Link to="/tickets/new" className="btn btn-primary hidden lg:flex">
          <Plus className="w-4 h-4" />
          Nuovo Ticket
        </Link>
      </div>

      {/* Quick Stats - Clickable */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 sm:overflow-visible sm:pb-0 sm:mx-0 sm:px-0 sm:justify-center">
        <StatCard 
          label="Urgenti" 
          value={urgentOpenCount} 
          icon={Flame} 
          color="red"
          filterPriority="urgent"
        />
        <StatCard 
          label="Aperti" 
          value={openCount} 
          icon={AlertCircle} 
          color="amber"
          filterStatus="open"
        />
        <StatCard 
          label="In Corso" 
          value={inProgressCount} 
          icon={Wrench} 
          color="blue"
          filterStatus="in_progress"
        />
        <StatCard 
          label="In Attesa" 
          value={waitingCount} 
          icon={Timer} 
          color="orange"
          filterStatus="waiting"
        />
      </div>

      {/* Filters Toolbar */}
      <div className="card card-sm space-y-2">
        {/* Desktop: all on one row, Mobile: search + status on first row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {/* Search and Status on same row on mobile */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-56 sm:flex-none">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => updateFilters({ search: e.target.value })}
                placeholder="Cerca ticket..."
                className="input input-sm pl-8 w-full"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => updateFilters({ status: e.target.value })}
              className="input input-sm w-auto min-w-0 flex-shrink sm:flex-1 sm:min-w-[80px]"
            >
              <option value="">Stato</option>
              {Object.entries(statusConfig).map(([value, config]) => (
                <option key={value} value={value}>{config.label}</option>
              ))}
            </select>
          </div>
          
          {/* Other filters row */}
          <div className="flex items-center gap-2 flex-1 flex-wrap sm:flex-nowrap">
            <select
              value={priorityFilter}
              onChange={(e) => updateFilters({ priority: e.target.value })}
              className="input input-sm w-auto min-w-0 flex-shrink sm:flex-1 sm:min-w-[80px]"
            >
              <option value="">Priorità</option>
              {Object.entries(priorityLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            
            <div className="flex-1 min-w-[100px] sm:min-w-[120px]">
              <LocationMultiSelect
                locations={locationsData?.locations || []}
                selected={locationFilter}
                onChange={(ids) => updateFilters({ locations: ids })}
              />
            </div>
          </div>
        </div>
        
        {/* Results count on right with reset button */}
        <div className="flex items-center justify-end gap-2 text-xs text-gray-500 pt-1 border-t border-gray-100">
          {hasFilters && (
            <button onClick={clearFilters} className="btn btn-ghost btn-xs flex items-center gap-1 text-primary-600 hover:text-primary-700">
              <X className="w-3 h-3" />
              Reset
            </button>
          )}
          <ResultsCount count={tickets.length} label="ticket" />
        </div>
      </div>

      {/* Data Table */}
      <div className="card card-sm">
        <DataTable
          data={tickets}
          columns={columns}
          keyExtractor={(t) => t.id}
          onRowClick={(t) => navigate(`/tickets/${t.id}`)}
          isLoading={isLoading}
          emptyMessage={hasFilters ? 'Nessun ticket corrisponde ai filtri' : 'Nessun ticket'}
          emptyIcon={<AlertCircle className="w-8 h-8" />}
          mobileCardRender={mobileCardRender}
        />
      </div>

    </div>
  );
}
